import _ from 'lodash'
import Cropper from 'react-easy-crop'
import { Loader2 } from 'lucide-react'
import { constructProxyUrl } from '@/utils/proxy'
import { useState, useEffect } from 'react'

const ImageCropper = ({
    url,
    aspectRatio,
    onComplete,
    croppedAreaPixels,
    croppedArea,
    type,
    startTime,
    endTime,
    videoDuration,
}) => {
    const [videoRef, setVideoRef] = useState(null)
    const [mediaLoaded, setMediaLoaded] = useState(false)
    const [crop, setCrop] = useState({ x: 0, y: 0 })
    const [zoom, setZoom] = useState(1)
    const proxyUrl = constructProxyUrl(url)

    useEffect(() => {
        if (croppedAreaPixels) {
            setCrop({ x: croppedAreaPixels.x, y: croppedAreaPixels.y })
            setZoom(croppedAreaPixels.zoom)
        }
    }, [croppedAreaPixels])

    useEffect(() => {
        if (type !== 'video' || !videoRef?.current) return

        const video = videoRef.current
        const safeStartTime = _.isNumber(startTime) && startTime >= 0 ? startTime : 0
        const safeEndTime = _.isNumber(endTime) && endTime > 0 ? endTime : videoDuration / 1000 || 0

        let hasSeekToStart = false

        const handleLoadedData = () => {
            if (hasSeekToStart) return

            const seekToStart = () => {
                if (video.readyState < 2) {
                    setTimeout(seekToStart, 50)
                    return
                }

                video.currentTime = safeStartTime
                hasSeekToStart = true
                // Wait for seek to complete then play
                const checkSeekComplete = () => {
                    if (Math.abs(video.currentTime - safeStartTime) < 0.1) {
                        // Autoplay might be blocked, user will need to click
                        video.play().catch(() => {})
                    } else {
                        setTimeout(checkSeekComplete, 50)
                    }
                }

                if (safeStartTime > 0) setTimeout(checkSeekComplete, 50)
                else video.play().catch(() => {}) // If starting from 0, play immediately
            }
            seekToStart()
        }

        const handleTimeUpdate = () => {
            const current = video.currentTime
            // Enforce segment boundaries
            if (current < safeStartTime) video.currentTime = safeStartTime
            else if (safeEndTime > 0 && current >= safeEndTime) video.currentTime = safeStartTime // Loop back to start
        }

        video.addEventListener('loadeddata', handleLoadedData)
        video.addEventListener('timeupdate', handleTimeUpdate)

        // Set video attributes for autoplay
        video.muted = true
        video.loop = true

        // Initial seek if video is already loaded
        if (video.readyState >= 2 && !hasSeekToStart) {
            video.currentTime = safeStartTime
            hasSeekToStart = true
            video.play().catch(() => {})
        }

        return () => {
            video.removeEventListener('loadeddata', handleLoadedData)
            video.removeEventListener('timeupdate', handleTimeUpdate)
        }
    }, [videoRef, startTime, endTime, videoDuration, type])

    return (
        <div className="tw:min-h-[400px] tw:relative">
            {!mediaLoaded && (
                <div className="tw:absolute tw:top-0 tw:left-0 tw:w-full tw:h-full tw:bg-black tw:opacity-50 tw:flex tw:justify-center tw:items-center">
                    <Loader2 className="tw:w-3 tw:h-3 tw:text-white tw:animate-spin" />
                    <span className="tw:text-sm tw:ml-1.5">Loading...</span>
                </div>
            )}
            <Cropper
                crop={crop}
                zoom={zoom}
                aspect={aspectRatio}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                setVideoRef={(ref) => {
                    setVideoRef(ref)

                    // console.log(ref)
                }}
                onMediaLoaded={() => setMediaLoaded(true)}
                initialCroppedAreaPercentages={croppedArea}
                {...(type === 'image' ? { image: proxyUrl } : { video: proxyUrl })}
                onCropComplete={(croppedArea, croppedAreaPixels) => onComplete(croppedArea, croppedAreaPixels, zoom)}
            />
        </div>
    )
}

export default ImageCropper
